import React, { useState } from 'react';

import { Box, TextField, Button, Typography, Alert } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import axiosWrapper from '../../utils/apiRequests/axiosWrapper';
import extractAuthFromApiBody from '../../utils/extractAuthFromApiBody';
import { updateUserInfo } from '../../features/user/userSlice';
import { hydrateUserData } from '../../utils/cache/dataHydration';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';

const LoginModal = ({ onClose, switchToRegister }) => {
  const [formInfo, setFormInfo] = useState({
    email: '',
    password: '',
  });
  const dispatch = useDispatch();

  const [errors, setErrors] = useState(null);
  const { colors } = useThemeStyles();

  const changeHandler = (e) => {
    setFormInfo({
      ...formInfo,
      [e.target.name]: e.target.value,
    });
  };

  const login = async (e) => {
    e.preventDefault();
    const response = await axiosWrapper({
      method: 'post',
      path: 'login',
      data: formInfo,
      optimistic: false,
    });

    if (response?.error || response?.queued) {
      setErrors(
        response?.error?.response?.data?.msg ||
          response?.error?.message ||
          'Could not reach the server. Check your connection and try again.'
      );
      return;
    }

    const { user: userForStore, token: storedToken } = extractAuthFromApiBody(response?.data);
    if (userForStore?._id) {
      if (!storedToken) {
        setErrors(
          'Login succeeded but no session token was returned. The API may use a different response shape in production.'
        );
        return;
      }
      localStorage.setItem('authToken', storedToken);
      dispatch(updateUserInfo(userForStore));

      // Hydrate user data into cache after successful login
      const userId = userForStore._id;
      if (userId) {
        hydrateUserData(userId).catch((err) => {
          console.error('[LoginModal] Error hydrating user data:', err);
          // Don't block login on hydration failure
        });
      }

      onClose();
    } else {
      setErrors(response?.data?.msg || 'Invalid credentials');
    }
  };

  return (
    <ReusableModal
      open
      onClose={onClose}
      title="Log in"
      width={420}
      showCloseButton
      ariaLabelledby="login-modal-title"
      ariaDescribedby="login-modal-description"
    >
      <Box
        component="form"
        onSubmit={login}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {errors && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {errors}
          </Alert>
        )}

        <TextField
          label="Email"
          name="email"
          type="email"
          fullWidth
          margin="normal"
          onChange={changeHandler}
          sx={{
            '& .MuiInputLabel-root': {
              color: colors?.text?.secondary || 'text.secondary',
            },
            '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
            input: { color: colors?.text?.primary || 'text.primary' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
              '&:hover fieldset': { borderColor: 'primary.main' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
              {
                WebkitBoxShadow: `0 0 0 100px ${
                  colors?.background?.surface || colors?.paper?.background || '#111827'
                } inset`,
                WebkitTextFillColor: colors?.text?.primary || 'inherit',
              },
          }}
        />

        <TextField
          label="Password"
          name="password"
          type="password"
          fullWidth
          margin="normal"
          onChange={changeHandler}
          sx={{
            '& .MuiInputLabel-root': {
              color: colors?.text?.secondary || 'text.secondary',
            },
            '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
            input: { color: colors?.text?.primary || 'text.primary' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
              '&:hover fieldset': { borderColor: 'primary.main' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
              {
                WebkitBoxShadow: `0 0 0 100px ${
                  colors?.background?.surface || colors?.paper?.background || '#111827'
                } inset`,
                WebkitTextFillColor: colors?.text?.primary || 'inherit',
              },
          }}
        />

        <Button type="submit" variant="contained" fullWidth sx={{ mt: 1 }}>
          Log in
        </Button>

        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          Don&apos;t have an account?{' '}
          <Typography
            component="span"
            onClick={switchToRegister}
            sx={{
              color: colors?.interactive?.primary || 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                color: colors?.interactive?.hover || 'primary.dark',
              },
            }}
          >
            Create one
          </Typography>
        </Typography>
      </Box>
    </ReusableModal>
  );
};

export default LoginModal;

LoginModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  switchToRegister: PropTypes.func.isRequired,
};
